import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";
import { sowingInstructionsApi } from "../services/sowingInstructionsApi";

export interface VarietyStockDto {
  cropTypeId?: string;
  cropTypeName: string;
  variety?: string;
  batchCount: number;
  totalQuantity: number;
  availableQuantity: number;
  reservedQuantity: number;
}

export const useStockByVariety = (cropTypeId?: string) => {
  return useQuery({
    queryKey: ["greenhouse-stock-by-variety", cropTypeId],
    queryFn: async () => {
      const url = cropTypeId
        ? `/greenhouse-stock/by-variety?cropTypeId=${cropTypeId}`
        : "/greenhouse-stock/by-variety";
      const { data } = await apiClient.get<ApiResponse<VarietyStockDto[]>>(url);
      return data.data ?? [];
    },
  });
};

export const useGreenhouseStockEntries = () => {
  return useQuery({
    queryKey: ["greenhouse-stock-entries"],
    queryFn: () => sowingInstructionsApi.getAllGreenhouseStockEntries(),
  });
};

export const useAvailableGreenhouseStock = (params?: {
  cropTypeId?: string;
  variety?: string;
  trayType?: number;
  search?: string;
  /**
   * When true the server filters to IsReadyForDispatch=true and lifts the
   * "must have GreenhouseId" requirement so agroforestry stock (no greenhouse)
   * also surfaces. Walk-in POS sets this; the in-greenhouse stock view does not.
   */
  readyForWalkIn?: boolean;
  source?: "Greenhouse" | "Agroforestry";
  /** Set false to skip querying (e.g. expo-only walk-in mode). */
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["greenhouse-stock-available", params],
    queryFn: () => sowingInstructionsApi.getAvailableGreenhouseStock(params),
    enabled: params?.enabled ?? true,
  });
};

export const usePagedAvailableGreenhouseStock = (params: {
  cropTypeId?: string;
  variety?: string;
  trayType?: number;
  search?: string;
  pageNumber: number;
  pageSize: number;
  readyForWalkIn?: boolean;
  source?: "Greenhouse" | "Agroforestry";
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["greenhouse-stock-available", "paged", params],
    queryFn: () => sowingInstructionsApi.getPagedAvailableGreenhouseStock(params),
    placeholderData: keepPreviousData,
    enabled: params.enabled ?? true,
  });
};

export const useReserveGreenhouseStock = () => {
  return useMutation({
    mutationFn: ({ entryId, orderId, orderItemId, quantity }: { entryId: string; orderId: string; orderItemId: string; quantity: number }) =>
      sowingInstructionsApi.reserveGreenhouseStock(entryId, orderId, quantity, orderItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-by-variety"] });
      queryClient.invalidateQueries({ queryKey: ["agroforestry-stock"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};
